// src/services/api.js
import axios from 'axios';
import { config } from '../config';

// Configuración base de axios
const api = axios.create({
    baseURL: config.api.baseURL,
    timeout: config.api.timeout,
});

// Interceptor para agregar el token de autenticación y headers de ngrok
api.interceptors.request.use(
    (config) => {
        // localStorage es la fuente principal (persiste entre pestañas/reinicios);
        // sessionStorage queda solo como fallback por compatibilidad con sesiones viejas.
        // Si el llamador ya puso su propio Authorization (ej. el token temporal de
        // activación forzada de MFA durante login, antes de tener sesión real), se
        // respeta y no se pisa con la sesión guardada.
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        if (token && !config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Agregar header para evitar warning de ngrok
        if (config.baseURL && config.baseURL.includes('ngrok-free.app')) {
            config.headers['ngrok-skip-browser-warning'] = 'true';
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Interceptor para manejar errores de respuesta
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('user');
            // Solo redirigir si no estamos ya en la página de login
            const basePath = (window.APP_CONFIG && window.APP_CONFIG.BASE_PATH !== undefined) ? window.APP_CONFIG.BASE_PATH : '/';
            const loginPath = basePath.endsWith('/') ? `${basePath}login` : `${basePath}/login`;
            if (window.location.pathname !== loginPath) {
                window.location.href = loginPath;
            }
        }
        return Promise.reject(error);
    }
);

// Función para verificar la salud de la API
export const checkApiHealth = async () => {
    try {
        await api.get('/actas/test');
        return true;
    } catch (error) {
        console.error('API health check failed:', error);
        return false;
    }
};

// Función para verificar la salud de la API de tickets para usuarios autenticados
export const checkTicketsApiHealth = async () => {
    try {
        await api.get('/tickets/health-user');
        return true;
    } catch (error) {
        console.error('Tickets API health check failed:', error);
        return false;
    }
};

// Métodos para Actas
export const actasAPI = {
    // CONSULTAS
    getMisActas: () => api.get('/actas/mis-actas'),
    getActasPendientesAprobacion: () => api.get('/actas/pendientes-aprobacion'),
    getTodasActas: () => api.get('/actas/todas'),
    getByAsignacionId: (asignacionId) => api.get(`/actas/asignacion/${asignacionId}`),
    getByMovimientoId: (movimientoId) => api.get(`/actas/movimiento/${movimientoId}`),
    getActasByActivo: (activoId) => api.get(`/actas/activo/${activoId}`),

    // ACCIONES DE ACTA
    generarActaAdmin: (data) => api.post('/actas/generar', data),
    previsualizarActaTemporal: (data) => api.post('/actas/previsualizar-temporal', data, { responseType: 'blob' }),
    marcarPendienteFirma: (data) => api.post('/actas/marcar-pendiente-firma', data),
    // firmar-digital recibe JSON (no multipart)
    firmarDigital: (data) => api.post('/actas/firmar-digital', data),
    // No forzar Content-Type: axios agrega el boundary automáticamente
    subirPdf: (formData) => api.post('/actas/subir-pdf', formData),
    subirActaAdmin: (formData) => api.post('/actas/subir-admin', formData),
    aprobarActa: (actaId, data) => api.post(`/actas/${actaId}/aprobar`, data),
    rechazarActa: (actaId, data) => api.post(`/actas/${actaId}/rechazar`, data),
    marcarPendiente: (actaId) => api.post(`/actas/${actaId}/pendiente`),
    anularActa: (actaId, data) => api.post(`/actas/${actaId}/anular`, data),
    previewAuto: (actaId) => api.get(`/actas/${actaId}/preview-auto`, { responseType: 'blob' }),

    // PREVISUALIZACIÓN Y DESCARGA
    previsualizarActa: (asignacionId) => api.get(`/actas/previsualizar/${asignacionId}`, { responseType: 'blob' }),
    previsualizarMovimiento: (movimientoId) => api.get(`/actas/previsualizar/0?movimientoId=${movimientoId}`, { responseType: 'blob' }),
    // En el backend Nest la ruta es /actas/:actaId/previsualizar-firmado
    previsualizarActaFirmado: (actaId) => api.get(`/actas/${actaId}/previsualizar-firmado`, { responseType: 'blob' }),
    descargarActa: (actaId) => api.get(`/actas/${actaId}/descargar`, { responseType: 'blob' }),
    previsualizarActaPersonalizada: (id, params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.incluirFirmaTI !== undefined) {
            queryParams.append('incluirFirmaTI', params.incluirFirmaTI.toString());
        }
        if (params.fechaEntrega) {
            queryParams.append('fechaEntrega', params.fechaEntrega);
        }
        const url = `/actas/previsualizar-personalizada/${id}?${queryParams}`;
        return api.get(url, { responseType: 'blob' });
    },

    subirFirma: (formData) => api.post('/actas/subir-firma', formData),
    eliminarActa: (actaId) => api.delete(`/actas/${actaId}`)
};

// Métodos para Asignaciones (incluye generación de actas)
export const asignacionesAPI = {
    getAll: () => api.get('/asignaciones'),
    getById: (id) => api.get(`/asignaciones/${id}`),
    getByActivo: (activoId) => api.get(`/asignaciones/activo/${activoId}`),
    getByUsuario: (usuarioId) => api.get(`/asignaciones/usuario/${usuarioId}`),
    getActivas: () => api.get('/asignaciones/activas'),
    create: (data) => api.post('/asignaciones', data),
    devolver: (asignacionId, data) => api.put(`/asignaciones/${asignacionId}/devolver`, data),
    generarActa: (asignacionId, params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.includeSignature !== undefined) {
            queryParams.append('includeSignature', params.includeSignature);
        }
        if (params.fechaEntrega) {
            queryParams.append('fechaEntrega', params.fechaEntrega);
        }
        return api.get(`/asignaciones/${asignacionId}/acta?${queryParams}`, { responseType: 'blob' });
    },
    subirActaFirmada: (asignacionId, formData) => api.post(`/asignaciones/${asignacionId}/acta-firmada`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    crearActasPendientes: () => api.post('/asignaciones/crear-actas-pendientes')
};

// Métodos para Usuarios
export const usuariosAPI = {
    getAll: () => api.get('/usuarios'),
    getById: (id) => api.get(`/usuarios/${id}`),
    create: (data) => api.post('/usuarios', data),
    update: (id, data) => api.put(`/usuarios/${id}`, data),
    delete: (id) => api.delete(`/usuarios/${id}`),
    getActivosAsignados: (usuarioId) => api.get(`/usuarios/${usuarioId}/activos-asignados`),
    getAllNomina: () => api.get('/usuarios'), // Reutilizamos el endpoint de usuarios que devuelve la nómina
    getPermisosCatalogo: () => api.get('/usuarios/permisos/catalogo'),
    getMisApps: () => api.get('/usuarios/me/apps'),
    getPermisosUsuario: (id) => api.get(`/usuarios/${id}/permisos`),
    otorgarPermiso: (id, data) => api.post(`/usuarios/${id}/permisos`, data),
    revocarPermiso: (permisoId) => api.delete(`/usuarios/permisos/${permisoId}`),
    // Cuentas locales (usuario + contraseña) para apps sin correo corporativo, ej. Exportaciones.
    cambiarScopeApp: (id, data) => api.put(`/usuarios/${id}/permisos/scope-app`, data),
    // Permisos granulares de una app (Muestras): por defecto del perfil + ajustes por persona
    getPermisosFinosApp: (id, appCodigo) => api.get(`/usuarios/${id}/app-permisos/${appCodigo}`),
    setPermisoFinoApp: (id, appCodigo, data) => api.put(`/usuarios/${id}/app-permisos/${appCodigo}`, data),
    getReemplazosApp: (id, appCodigo) => api.get(`/usuarios/${id}/app-permisos/${appCodigo}/reemplazos`),
    setReemplazosApp: (id, appCodigo, data) => api.put(`/usuarios/${id}/app-permisos/${appCodigo}/reemplazos`, data),
    setDatosApp: (id, appCodigo, data) => api.put(`/usuarios/${id}/app-permisos/${appCodigo}/datos`, data),
    setRolApp: (id, appCodigo, data) => api.put(`/usuarios/${id}/app-permisos/${appCodigo}/rol`, data),
    restablecerPermisosFinosApp: (id, appCodigo) => api.delete(`/usuarios/${id}/app-permisos/${appCodigo}`),
  listarCuentasLocalesApp: (appCodigo) => api.get(`/usuarios/cuentas-locales/${appCodigo}`),
    crearCuentaLocalApp: (appCodigo, data) => api.post(`/usuarios/cuenta-local/${appCodigo}`, data),
    resetPasswordCuentaLocal: (apiUserId, password) => api.put(`/usuarios/cuenta-local/${apiUserId}/password`, { password }),
    toggleActivoCuentaLocal: (apiUserId, activo) => api.put(`/usuarios/cuenta-local/${apiUserId}/activo`, { activo }),
    eliminarCuentaLocal: (apiUserId) => api.delete(`/usuarios/cuenta-local/${apiUserId}`),
};

// Métodos para Activos
export const activosAPI = {
    getAll: () => api.get('/activos'),
    getById: (id) => api.get(`/activos/${id}`),
    create: (data) => api.post('/activos', data),
    update: (id, data) => api.put(`/activos/${id}`, data),
    delete: (id) => api.delete(`/activos/${id}`),
    getDisponibles: () => api.get('/activos/disponibles'),
    getAsignados: () => api.get('/activos/asignados'),
    getEnMantenimiento: () => api.get('/activos/en-mantenimiento'),
    getRetirados: () => api.get('/activos/retirados'),
    getMisActivos: () => api.get('/activos/mis-activos'),
    getMarcas: (categoria) => api.get('/activos/marcas', { params: categoria ? { categoria } : {} }),
    darBaja: (id, motivo) => api.put(`/activos/${id}/dar-baja`, { motivoBaja: motivo }),
    updateRustDeskId: (activoId, rustDeskId) => api.patch(`/activos/${activoId}/rustdesk-id`, { rustDeskId }),
    updateRustDeskPassword: (activoId, password) => api.patch(`/activos/${activoId}/rustdesk-password`, { password }),
    updateAnyDeskId: (activoId, anyDeskId) => api.patch(`/activos/${activoId}/anydesk-id`, { anyDeskId }),
    updateAnyDeskPassword: (activoId, password) => api.patch(`/activos/${activoId}/anydesk-password`, { password }),
    updateIpAddress: (activoId, ipAddress) => api.patch(`/activos/${activoId}/ip-address`, { ipAddress })
};

// Métodos para Tickets
export const ticketsAPI = {
    getAll: () => api.get('/tickets'),
    getById: (id) => api.get(`/tickets/${id}`),
    create: (data) => api.post('/tickets', data),
    createTicket: (data) => api.post('/tickets', data), // Alias para compatibilidad
    update: (id, data) => api.put(`/tickets/${id}`, data),
    delete: (id) => api.delete(`/tickets/${id}`),
    getMisTickets: () => api.get('/tickets/mis-tickets-usuario'),
    getComentarios: (ticketId) => api.get(`/tickets/${ticketId}/comentarios`),
    agregarComentario: (ticketId, data) => api.post(`/tickets/${ticketId}/comentarios`, data),
    subirArchivo: (ticketId, formData) => api.post(`/tickets/${ticketId}/archivos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    uploadEvidence: (formData) => api.post('/tickets/upload-evidence', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    uploadMultipleFiles: (ticketId, formData) => api.post(`/tickets/${ticketId}/archivos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    getTicketFiles: (ticketId) => api.get(`/tickets/${ticketId}/archivos`),
    deleteTicketFile: (ticketId, fileId) => api.delete(`/tickets/${ticketId}/archivos/${fileId}`),
    descargarArchivo: (ticketId, archivoId) => api.get(`/tickets/${ticketId}/archivos/${archivoId}`, { responseType: 'blob' }),
    getEstadisticas: () => api.get('/tickets/estadisticas')
};

// Métodos para Categorías de Tickets
export const categoriasTicketAPI = {
    getCategorias: () => api.get('/categoriasticket'),
    createCategoria: (data) => api.post('/categoriasticket', data),
    updateCategoria: (id, data) => api.put(`/categoriasticket/${id}`, data),
    deleteCategoria: (id) => api.delete(`/categoriasticket/${id}`),
    createSubcategoria: (categoriaId, data) => api.post(`/categoriasticket/${categoriaId}/subcategorias`, data),
    updateSubcategoria: (id, data) => api.put(`/categoriasticket/subcategorias/${id}`, data),
    deleteSubcategoria: (id) => api.delete(`/categoriasticket/subcategorias/${id}`)
};

// Métodos para Dashboard
export const dashboardAPI = {
    getStats: () => api.get('/dashboard'),
    getRecentActivity: () => api.get('/dashboard/recent-activity'),
    getChartData: () => api.get('/dashboard/chart-data')
};

// Métodos para Autenticación
export const authAPI = {
    login: (credentials) => api.post('/auth/login', credentials),
    verifyMfaLogin: (data) => api.post('/auth/mfa/verify-login', data),
    mfaStatus: () => api.get('/auth/mfa/status'),
    mfaSetup: () => api.post('/auth/mfa/setup'),
    mfaConfirmSetup: (codigo) => api.post('/auth/mfa/confirm-setup', { codigo }),
    mfaDisable: (password) => api.post('/auth/mfa/disable', { password }),
    // Activación forzada de MFA durante login (cuenta local sin MFA activado
    // todavía): usa el mfaSetupToken temporal en vez de la sesión guardada,
    // porque todavía no existe una sesión real.
    mfaSetupForzado: (mfaSetupToken) =>
        api.post('/auth/mfa/setup', {}, { headers: { Authorization: `Bearer ${mfaSetupToken}` } }),
    mfaConfirmSetupForzado: (mfaSetupToken, codigo) =>
        api.post('/auth/mfa/confirm-setup', { codigo }, { headers: { Authorization: `Bearer ${mfaSetupToken}` } }),
    sso: (assertion) => api.post('/auth/sso', { assertion }),
    // Crear usuario independiente (admin y soporte). El backend forzará rol 'usuario' para soporte
    register: (userData) => api.post('/auth/create-independent-user', userData),
    refresh: () => api.post('/auth/refresh'),
    logout: () => api.post('/auth/logout'),
    getProfile: () => api.get('/auth/me'),
    getUsuarios: () => api.get('/auth/usuarios'),
    updateProfile: (data) => api.put('/auth/profile', data),
    updateUser: (id, data) => api.put(`/auth/${id}`, data),
    getUsuariosCentrales: () => api.get('/auth/usuarios-centrales'),
    actualizarUsuarioCentral: (centralId, dto) => api.put(`/auth/usuarios-centrales/${centralId}`, dto),
    changePassword: (data) => api.put('/auth/change-password', data),
    // No forzar Content-Type: axios agrega el boundary automáticamente
    uploadSignature: (formData) => api.post('/auth/upload-signature', formData)
};

// Métodos para Reportes
export const reportesAPI = {
    getActivosReport: (filters = {}) => api.get('/reportes/activos', { params: filters }),
    getAsignacionesReport: (filters = {}) => api.get('/reportes/asignaciones', { params: filters }),
    getTicketsReport: (filters = {}) => api.get('/reportes/tickets', { params: filters }),
    getTiPerformance: (params = {}) => api.get('/reportes/ti-performance', { params }),
    getResenasAgente: (id, params = {}) => api.get(`/reportes/agente/${id}/resenas`, { params }),
    exportToExcel: (reportType, filters = {}) => api.get(`/reportes/${reportType}/excel`, {
        params: filters,
        responseType: 'blob'
    }),
    // Nuevo reporte trimestral
    getRegistrosTrimestrales: (trimestre, año) => api.get('/reportes/registros-trimestrales/excel', {
        params: { trimestre, año },
        responseType: 'blob'
    })
};

// Métodos para Calendario (admin/soporte)
export const calendarioAPI = {
    getEvents: (params = {}) => api.get('/calendario/events', { params }),
    create: (data) => api.post('/calendario', data),
    getById: (id) => api.get(`/calendario/${id}`),
    update: (id, data) => api.put(`/calendario/${id}`, data),
    remove: (id) => api.delete(`/calendario/${id}`)
};

// Métodos para Notificaciones
export const notificacionesAPI = {
    getMisNotificaciones: () => api.get('/notifications'),
    marcarComoLeida: (notificacionId) => api.post('/notifications/read', { ids: [notificacionId] }),
    marcarTodasComoLeidas: () => api.post('/notifications/read', { ids: [] }),
    getNoLeidas: () => api.get('/notifications?isRead=false')
};

// Métodos para Chat
export const chatAPI = {
    getConversaciones: () => api.get('/chat/conversaciones'),
    getConversacion: (id) => api.get(`/chat/conversaciones/${id}`),
    getSoporteDisponible: () => api.get('/chat/soporte-disponible'),
    crearConversacion: (data) => api.post('/chat/conversaciones', data),
    getMensajes: (conversacionId) => api.get(`/chat/conversaciones/${conversacionId}/mensajes`),
    enviarMensaje: (conversacionId, data) => api.post(`/chat/conversaciones/${conversacionId}/mensajes`, data),
    asignarSoporte: (conversacionId, data) => api.put(`/chat/conversaciones/${conversacionId}/asignar`, data),
    cerrarConversacion: (conversacionId) => api.put(`/chat/conversaciones/${conversacionId}/cerrar`),
    generarTicket: (conversacionId, data) => api.post(`/chat/conversaciones/${conversacionId}/generar-ticket`, data),
    getActivosUsuario: (conversacionId) => api.get(`/chat/conversaciones/${conversacionId}/activos-usuario`),

    // Nuevas funcionalidades para gestión de chats y mensajes
    archivarConversacion: (conversacionId) => api.put(`/chat/conversaciones/${conversacionId}/archivar`),
    desarchivarConversacion: (conversacionId) => api.put(`/chat/conversaciones/${conversacionId}/desarchivar`),
    eliminarConversacion: (conversacionId) => api.delete(`/chat/conversaciones/${conversacionId}`),
    eliminarMensaje: (mensajeId) => api.delete(`/chat/mensajes/${mensajeId}`),
    getConversacionesArchivadas: () => api.get('/chat/conversaciones/archivadas'),
    marcarMensajesComoLeidos: (conversacionId) => api.post(`/chat/${conversacionId}/marcar-leidos`)
};

// Métodos para Software y Seguridad
export const softwareSecurityAPI = {
    // Obtener todo el software y seguridad de un activo
    getByActivo: (activoId) => api.get(`/SoftwareSecurity/activo/${activoId}`),

    // Software
    createSoftware: (data) => api.post('/SoftwareSecurity/software', data),
    getSoftware: (id) => api.get(`/SoftwareSecurity/software/${id}`),
    updateSoftware: (id, data) => api.put(`/SoftwareSecurity/software/${id}`, data),
    deleteSoftware: (id) => api.delete(`/SoftwareSecurity/software/${id}`),

    // Programas de Seguridad
    createProgramaSeguridad: (data) => api.post('/SoftwareSecurity/seguridad', data),
    getProgramaSeguridad: (id) => api.get(`/SoftwareSecurity/seguridad/${id}`),
    updateProgramaSeguridad: (id, data) => api.put(`/SoftwareSecurity/seguridad/${id}`, data),
    deleteProgramaSeguridad: (id) => api.delete(`/SoftwareSecurity/seguridad/${id}`),

    // Licencias
    createLicencia: (data) => api.post('/SoftwareSecurity/licencia', data),
    getLicencia: (id) => api.get(`/SoftwareSecurity/licencia/${id}`),
    updateLicencia: (id, data) => api.put(`/SoftwareSecurity/licencia/${id}`, data),
    deleteLicencia: (id) => api.delete(`/SoftwareSecurity/licencia/${id}`)
};

// Métodos para Programas Estándar
export const programasEstandarAPI = {
    getAll: () => api.get('/programasestandar'),
    getByCategoria: (categoria) => api.get(`/programasestandar?categoria=${categoria}`),
    getCategorias: () => api.get('/programasestandar/categorias'),
    verificarInstalacion: (data) => api.post('/programasestandar/verificar-instalacion', data)
};

// Métodos para Paz y Salvo - Nuevo Sistema de Flujo de Firmas
export const pazYSalvoAPI = {
    // CRUD básico
    getAll: (params = {}) => api.get('/pazysalvo', { params }),
    getById: (id) => api.get(`/pazysalvo/${id}`),
    create: (data) => api.post('/pazysalvo', data),
    update: (id, data) => api.put(`/pazysalvo/${id}`, data),
    delete: (id) => api.delete(`/pazysalvo/${id}`),

    // Flujo de firmas
    enviarAFirma: (id) => api.post(`/pazysalvo/${id}/send`),
    firmar: (id, rol, data) => api.post(`/pazysalvo/${id}/firmas/${rol}/sign`, data),
    observar: (id, rol, data) => api.post(`/pazysalvo/${id}/firmas/${rol}/observe`, data),
    rechazar: (id, rol, data) => api.post(`/pazysalvo/${id}/firmas/${rol}/reject`, data),
    solicitarFirma: (id, rol) => api.post(`/pazysalvo/${id}/solicitar-firma/${rol}`),
    cerrar: (id) => api.post(`/pazysalvo/${id}/cerrar`),
    cambiarEstado: (id, data) => api.post(`/pazysalvo/${id}/cambiar-estado`, data),

    // Excepciones y adjuntos
    crearExcepcion: (id, data) => api.post(`/pazysalvo/${id}/excepciones`, data),
    subirAdjunto: (id, formData) => api.post(`/pazysalvo/${id}/adjuntos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),

    // Descarga y verificación
    descargarPdf: (id) => api.get(`/pazysalvo/${id}/pdf`, { responseType: 'blob' }),
    verificar: (hash) => api.get(`/pazysalvo/verify`, { params: { hash } }),

    // Métodos legacy (para compatibilidad)
    eliminar: (id) => api.delete(`/pazysalvo/${id}`),
    download: (id) => api.get(`/pazysalvo/${id}/pdf`, { responseType: 'blob' }),
    preview: (id) => api.get(`/pazysalvo/${id}/pdf`, { responseType: 'blob' }),
    getActivosPendientes: (usuarioId) => api.get(`/pazysalvo/activos-pendientes/${usuarioId}`),
    getActivosPendientesTodos: () => api.get('/pazysalvo/activos-pendientes-todos'),
    marcarActivoDevuelto: (data) => api.post('/pazysalvo/marcar-activo-devuelto', data),

    // Delegaciones
    getDelegations: () => api.get('/pazysalvoroles/delegations'),
    createDelegation: (data) => api.post('/pazysalvoroles/delegations', data),
    revokeDelegation: (delegationId) => api.delete(`/pazysalvoroles/delegations/${delegationId}`),
    getDelegationUsers: () => api.get('/pazysalvoroles/delegation-users'),

    // Subroles del usuario actual
    getUserSubroles: () => api.get('/pazysalvoroles/user-subroles'),
    getEmpresasConfig: () => api.get('/pazysalvoroles/empresas'),
    getAssignments: () => api.get('/pazysalvoroles/assignments'),
    createAssignment: (data) => api.post('/pazysalvoroles/assignments', data),
    updateAssignment: (id, data) => api.put(`/pazysalvoroles/assignments/${id}`, data),
    deleteAssignment: (id) => api.delete(`/pazysalvoroles/assignments/${id}`),
    getUsers: () => api.get('/pazysalvoroles/users'),
    getConfig: () => api.get('/pazysalvoroles/config'),
};

export const systemConfigAPI = {
    getConfig: () => api.get('/systemconfig'),
    updateConfig: (config) => api.put('/systemconfig', config),
    createBackup: () => api.post('/systemconfig/backup'),
    toggleMaintenance: (enabled, message) => api.post('/systemconfig/maintenance', { enabled, message }),
    getStats: () => api.get('/systemconfig/stats'),
    initializeConfig: () => api.post('/systemconfig/initialize'),
};



// Métodos para Consumibles
export const consumiblesAPI = {
    getAll: () => api.get('/consumibles'),
    getById: (id) => api.get(`/consumibles/${id}`),
    create: (data) => api.post('/consumibles', data),
    update: (id, data) => api.put(`/consumibles/${id}`, data),
    delete: (id) => api.delete(`/consumibles/${id}`),
    deleteMovimiento: (id) => api.delete(`/consumibles/movimiento/${id}`),
    getMovimiento: (id) => api.get(`/consumibles/movimiento/${id}`),
    registrarMovimiento: (data) => api.post('/consumibles/movimiento', data),
    getMovimientos: (id) => api.get(`/consumibles/${id}/movimientos`),
    uploadImage: (id, formData) => api.post(`/consumibles/${id}/imagen`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    deleteImage: (id) => api.delete(`/consumibles/${id}/imagen`),
    downloadComprobante: (movimientoId) => api.get(`/consumibles/comprobante/${movimientoId}`, { responseType: 'blob' }),
    getMisConsumibles: () => api.get('/consumibles/mis-consumibles'),
    getByUsuario: (userId) => api.get(`/consumibles/usuario/${userId}`),
    getSalidas: () => api.get('/consumibles/salidas')
};

// Métodos para Mi Equipo (Jerarquía Organizacional)
export const miEquipoAPI = {
    getEquipo: () => api.get('/mi-equipo'),
    getEquipoPorEmail: (email) => api.get('/mi-equipo/por-email', { params: { email } }),
    getActivos: () => api.get('/mi-equipo/activos'),
    asignarOperacional: (data) => api.post('/mi-equipo/asignar-operacional', data),
    desasignarOperacional: (id) => api.delete(`/mi-equipo/asignacion-operacional/${id}`),
    devolver: (id, data) => api.put(`/mi-equipo/asignacion/${id}/devolver`, data),
};

// Métodos para Áreas Organizacionales
export const areasAPI = {
    getAll: () => api.get('/areas'),
    getTree: () => api.get('/areas/tree'),
    getById: (id) => api.get(`/areas/${id}`),
    create: (data) => api.post('/areas', data),
    update: (id, data) => api.put(`/areas/${id}`, data),
    delete: (id) => api.delete(`/areas/${id}`),
    getMiembros: (id) => api.get(`/areas/${id}/miembros`),
    getJefeDirecto: (usuarioId) => api.get(`/areas/jefe/${usuarioId}`),
    createCargo: (data) => api.post('/areas/cargos', data),
    updateCargo: (id, data) => api.put(`/areas/cargos/${id}`, data),
    deleteCargo: (id) => api.delete(`/areas/cargos/${id}`),
};

// Métodos para Microsoft 365 / Active Directory
export const microsoftO365API = {
    getUserDetails: (emailOrUpn) => api.get(`/microsoft-o365/user-details/${encodeURIComponent(emailOrUpn)}`),
    changePassword: (data) => api.post('/microsoft-o365/change-password', data),
    resetMfa: (data) => api.post('/microsoft-o365/reset-mfa', data),
    manageAliases: (data) => api.post('/microsoft-o365/manage-aliases', data),
    configureForwarding: (data) => api.post('/microsoft-o365/configure-forwarding', data),
    convertSharedMailbox: (data) => api.post('/microsoft-o365/convert-shared-mailbox', data),
    configureOutOfOffice: (data) => api.post('/microsoft-o365/configure-out-of-office', data),
};

export { api };
export default api;

// Panel de control de aplicaciones (solo admin): usuarios con acceso, actividad y cambios de permisos
export const aplicacionesAPI = {
    getResumen: () => api.get('/aplicaciones'),
    getDetalle: (codigo) => api.get(`/aplicaciones/${encodeURIComponent(codigo)}`),
    getPerfiles: (codigo) => api.get(`/aplicaciones/${encodeURIComponent(codigo)}/perfiles`),
};
